import type { Action, ActionResult } from '../types/actions';
import { ActionValidator } from './action-validator';
import { executeClickAction } from './click-action';
import { executeFillAction } from './fill-action';
import { executeNavigateAction } from './navigate-action';
import { executeSubmitAction } from './submit-action';
import { executeScrollAction } from './scroll-action';
import { executeSelectAction } from './select-action';
import { executeHoverAction } from './hover-action';
import { executeFocusAction, executeBlurAction } from './focus-blur-action';
import { executeConnectToAgentAction } from './connect-to-agent-action';
import { executeVisualHighlightAction } from './visual-highlight-action';
import { EventBus } from '../core/event-bus';
import { Logger } from '../core/logger';
import type { UIHighlighter } from '../highlighter/ui-highlighter';

export class ActionExecutor {
  private validator: ActionValidator;
  private eventBus: EventBus;
  private logger: Logger;
  private highlighter: UIHighlighter | null;

  constructor(eventBus: EventBus, logger: Logger, highlighter: UIHighlighter | null = null) {
    this.validator = new ActionValidator();
    this.eventBus = eventBus;
    this.logger = logger;
    this.highlighter = highlighter;
  }

  /**
   * Execute an action
   */
  async execute(action: Action): Promise<ActionResult> {
    this.logger.info(`Executing action: ${action.type}`);
    this.eventBus.emit('action:start', action);

    // Validate action
    const validation = this.validator.validate(action);
    if (!validation.valid) {
      const result: ActionResult = {
        success: false,
        action,
        error: `Validation failed: ${validation.errors.join(', ')}`,
        timestamp: Date.now(),
        duration: 0
      };
      this.eventBus.emit('action:failed', result);
      return result;
    }

    // Log warnings
    if (validation.warnings.length > 0) {
      this.logger.warn('Action warnings:', validation.warnings);
    }

    // Execute action based on type
    let result: ActionResult;
    try {
      switch (action.type) {
        case 'click':
          result = await executeClickAction(action);
          break;
        case 'fill':
          result = await executeFillAction(action);
          break;
        case 'navigate':
          result = await executeNavigateAction(action);
          break;
        case 'submit':
          result = await executeSubmitAction(action);
          break;
        case 'scroll':
          result = await executeScrollAction(action);
          break;
        case 'select':
          result = await executeSelectAction(action);
          break;
        case 'hover':
          result = await executeHoverAction(action);
          break;
        case 'focus':
          result = await executeFocusAction(action);
          break;
        case 'blur':
          result = await executeBlurAction(action);
          break;
        case 'connect_to_agent':
          result = await executeConnectToAgentAction(action);
          break;
        case 'visualHighlight':
          if (!this.highlighter) {
            result = {
              success: false,
              action,
              error: 'visualHighlight action requires a UIHighlighter instance (none provided)',
              timestamp: Date.now(),
              duration: 0,
            };
          } else {
            result = await executeVisualHighlightAction(action, this.highlighter);
          }
          break;
        default:
          result = {
            success: false,
            action,
            error: `Action type not implemented: ${(action as any).type}`,
            timestamp: Date.now(),
            duration: 0
          };
      }
    } catch (error) {
      result = {
        success: false,
        action,
        error: error instanceof Error ? error.message : String(error),
        timestamp: Date.now(),
        duration: 0
      };
    }

    // Emit events
    if (result.success) {
      this.logger.info(`Action completed successfully: ${action.type} (${result.duration}ms)`);
      this.eventBus.emit('action:success', result);
    } else {
      this.logger.error(`Action failed: ${action.type} - ${result.error}`);
      this.eventBus.emit('action:failed', result);
    }

    this.eventBus.emit('action:complete', result);

    return result;
  }

  /**
   * Execute multiple actions in sequence
   */
  async executeSequence(actions: Action[]): Promise<ActionResult[]> {
    const results: ActionResult[] = [];

    for (const action of actions) {
      const result = await this.execute(action);
      results.push(result);

      // Stop on first failure if action is critical
      if (!result.success) {
        this.logger.warn('Action sequence stopped due to failure');
        break;
      }
    }

    return results;
  }

  /**
   * Validate an action without executing
   */
  validateAction(action: Action) {
    return this.validator.validate(action);
  }
}
